import React, { useState, useEffect, useRef } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Tooltip } from "@material-ui/core";
import MicIcon from "@material-ui/icons/Mic";

const useStyles = makeStyles(theme => ({
	wrapper: {
		display: "flex",
		alignItems: "center",
		flex: 1,
		gap: 12,
		overflow: "hidden",
		minWidth: 0,
	},
	timerBox: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		color: theme.palette.type === "dark" ? "#e9edef" : "#111b21",
		fontSize: 14,
		fontVariantNumeric: "tabular-nums",
		fontWeight: 500,
		flexShrink: 0,
	},
	dot: {
		width: 8,
		height: 8,
		borderRadius: "50%",
		backgroundColor: "#f15c6d",
		animation: "$pulse 1.4s ease-in-out infinite",
		flexShrink: 0,
	},
	"@keyframes pulse": {
		"0%": { opacity: 1, transform: "scale(1)" },
		"50%": { opacity: 0.35, transform: "scale(0.85)" },
		"100%": { opacity: 1, transform: "scale(1)" },
	},
	waveformWrapper: {
		flex: 1,
		height: 28,
		display: "flex",
		alignItems: "center",
		position: "relative",
		overflow: "hidden",
		minWidth: 60,
	},
	canvas: {
		width: "100%",
		height: "100%",
		display: "block",
	},
	micBadge: {
		display: "flex",
		alignItems: "center",
		backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
		borderRadius: 12,
		padding: "2px 8px",
		maxWidth: 160,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		flexShrink: 0,
		cursor: "default",
		transition: "background 0.2s, border-color 0.2s",
		border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
	},
	micText: {
		fontSize: 11,
		color: theme.palette.type === "dark" ? "#8696a0" : "#54656f",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		fontWeight: 500,
	},
}));

const RecordingTimer = ({ audioStream }) => {
	const classes = useStyles();
	const theme = useTheme();
	const isDark = theme.palette.type === "dark";

	const [seconds, setSeconds] = useState(0);
	const [hasVoice, setHasVoice] = useState(false);
	const [micLabel, setMicLabel] = useState("");

	const canvasRef = useRef(null);
	const audioContextRef = useRef(null);
	const analyserRef = useRef(null);
	const sourceRef = useRef(null);
	const animationFrameRef = useRef(null);
	const prevHeightsRef = useRef([]);

	// Timer counter
	useEffect(() => {
		const timer = setInterval(() => setSeconds(s => s + 1), 1000);
		return () => clearInterval(timer);
	}, []);

	// Detect active microphone device label
	useEffect(() => {
		if (audioStream && typeof audioStream.getAudioTracks === "function") {
			const track = audioStream.getAudioTracks()[0];
			if (track && track.label) {
				setMicLabel(track.label);
			}
		}
	}, [audioStream]);

	// Real AudioContext and AnalyserNode setup
	useEffect(() => {
		if (!audioStream) return;

		let isMounted = true;
		try {
			const AudioCtx = window.AudioContext || window.webkitAudioContext;
			if (!AudioCtx) return;

			const audioCtx = new AudioCtx();
			audioContextRef.current = audioCtx;

			const analyser = audioCtx.createAnalyser();
			analyser.fftSize = 64; // 32 frequency bins
			analyser.smoothingTimeConstant = 0.6;
			analyserRef.current = analyser;

			const source = audioCtx.createMediaStreamSource(audioStream);
			sourceRef.current = source;
			source.connect(analyser);

			const bufferLength = analyser.frequencyBinCount;
			const dataArray = new Uint8Array(bufferLength);

			let silenceCounter = 0;

			const render = () => {
				if (!isMounted) return;

				animationFrameRef.current = requestAnimationFrame(render);
				analyser.getByteFrequencyData(dataArray);

				// Calculate average volume energy
				let total = 0;
				for (let i = 0; i < bufferLength; i++) {
					total += dataArray[i];
				}
				const avg = total / bufferLength;
				const isDetectingVoice = avg > 5;

				if (isDetectingVoice) {
					silenceCounter = 0;
					setHasVoice(true);
				} else {
					silenceCounter++;
					if (silenceCounter > 15) {
						setHasVoice(false);
					}
				}

				// Draw on canvas
				const canvas = canvasRef.current;
				if (!canvas) return;

				const ctx = canvas.getContext("2d");
				if (!ctx) return;

				const dpr = window.devicePixelRatio || 1;
				const width = canvas.clientWidth;
				const height = canvas.clientHeight;

				if (canvas.width !== Math.floor(width * dpr) || canvas.height !== Math.floor(height * dpr)) {
					canvas.width = Math.floor(width * dpr);
					canvas.height = Math.floor(height * dpr);
				}

				ctx.save();
				ctx.scale(dpr, dpr);
				ctx.clearRect(0, 0, width, height);

				const barWidth = 3;
				const gap = 2.5;
				const slot = barWidth + gap;
				const numBars = Math.max(10, Math.floor(width / slot));
				const totalW = numBars * slot - gap;
				const startX = Math.max(0, (width - totalW) / 2);

				if (prevHeightsRef.current.length !== numBars) {
					prevHeightsRef.current = new Array(numBars).fill(3);
				}

				for (let i = 0; i < numBars; i++) {
					// Map to frequency spectrum (skip upper silent frequencies)
					const binIndex = Math.floor((i / numBars) * (bufferLength * 0.75));
					const rawVal = isDetectingVoice ? (dataArray[binIndex] || 0) : 0;
					const targetH = isDetectingVoice
						? 3 + (rawVal / 255) * (height - 6)
						: 3;

					// Smooth animation transition
					prevHeightsRef.current[i] = prevHeightsRef.current[i] * 0.4 + targetH * 0.6;
					const currentH = Math.max(3, Math.min(height - 2, prevHeightsRef.current[i]));

					const x = startX + i * slot;
					const y = (height - currentH) / 2;

					ctx.fillStyle = isDetectingVoice
						? "#00a884"
						: (isDark ? "rgba(255, 255, 255, 0.22)" : "rgba(0, 0, 0, 0.2)");

					ctx.beginPath();
					if (ctx.roundRect) {
						ctx.roundRect(x, y, barWidth, currentH, 1.5);
					} else {
						ctx.rect(x, y, barWidth, currentH);
					}
					ctx.fill();
				}

				ctx.restore();
			};

			render();
		} catch (err) {
			console.error("Error setting up audio visualizer:", err);
		}

		return () => {
			isMounted = false;
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
			if (sourceRef.current) {
				sourceRef.current.disconnect();
			}
			if (audioContextRef.current && audioContextRef.current.state !== "closed") {
				audioContextRef.current.close().catch(() => {});
			}
		};
	}, [audioStream, isDark]);

	const fmt = n => String(Math.floor(n)).padStart(2, "0");
	const mins = Math.floor(seconds / 60);
	const secs = seconds % 60;

	return (
		<div className={classes.wrapper}>
			<div className={classes.timerBox}>
				<span className={classes.dot} />
				{`${fmt(mins)}:${fmt(secs)}`}
			</div>

			<div className={classes.waveformWrapper}>
				<canvas ref={canvasRef} className={classes.canvas} />
			</div>

			{micLabel && (
				<Tooltip
					title={`Microfone em uso: ${micLabel} (${hasVoice ? "Áudio detectado" : "Nenhum som no momento"})`}
					placement="top"
				>
					<div className={classes.micBadge}>
						<MicIcon
							style={{
								fontSize: 14,
								marginRight: 4,
								color: hasVoice ? "#00a884" : "#f15c6d",
								transition: "color 0.2s",
							}}
						/>
						<span className={classes.micText}>
							{micLabel}
						</span>
					</div>
				</Tooltip>
			)}
		</div>
	);
};

export default RecordingTimer;